/**
 * `AIGuardrailsModel.StepMove.ts` — the agent moves a guided run only when the
 * learner asked it to (AI-1412; "Spark Voice Transport Improvements", issue 2).
 *
 * Web-only for now: the headset has the same gap and needs this rule ported.
 * Owned by `AIGuardrailsModel` and reached through its `stepMoves`.
 *
 * **Why it is code, when `Campfire_Web_AI.md` prefers a prompt fix.** Background
 * noise ends a turn, and the realtime model answers the AUDIO, not the
 * transcript. It hears a confident phrase in a muffled sound, and nothing in
 * the prompt can reach a mishearing the model is sure of. Measured 2026-09-24:
 * a muffled "Goodbye." moved the run 5/5 on `main` (1 next, 4 replays), and
 * three prompt rules about unclear audio still let it move 7/10. The transcript
 * comes from a separate model, and it is what this check reads.
 *
 * **How.**
 *   - The orchestrator reports every LEARNER turn here: typed text (a tapped
 *     `present_options` choice arrives as typed text too), and each spoken turn
 *     by its conversation item id, with its transcript when that lands. Nothing
 *     else reaches these methods — a step's narration, a context note or a
 *     tool's fixed reply is never the learner asking for anything.
 *   - `WorkflowExecutor` calls {@link StepMoveGuardrail.checkAsync} immediately
 *     before it would move the run, after its own structural refusals.
 *   - The check reads the LATEST learner turn. A spoken turn's transcript can
 *     land after the model has already called the tool (about 1.1 s after, in
 *     2 of the 9 moves in the AI-1392 traces), so the check waits for it, up to
 *     {@link K_STEP_MOVE_TRANSCRIPT_WAIT_MS}. A partial transcript that already
 *     asks, and has reached the end of a sentence, is enough to let the move
 *     go; only the finished one can refuse it.
 *   - The words are judged by `stepMoveWords.ts` — a noise filter, not an
 *     intent classifier.
 *   - **A request for a SCENE is refused as one** (`scene_request`, user
 *     direction 2026-10-21): during a run each step brings its own scene, and
 *     the learner moves by steps only. "Go to the next scene" had moved the run
 *     on, and "the welcome scene" jumped back to the step called "Welcome". It
 *     is checked first, before the other-language bypass too, since a scene
 *     cannot be asked for in any language.
 *
 * **What it does not check**: a replay (user direction, 2026-09-28: "Replay
 * should replay this step"), the learner's own buttons and Outline, a script's
 * commands, and MCP calls. None of those reach the check. So "replay this
 * scene" replays the step, which changes no scene.
 */

import {
  asksForScene,
  endsWithQuestion,
  isConfirmRequest,
  isMoveQuestion,
  isQuestionsQuestion,
  isSoundsOnly,
  readStepMoveWords,
  type StepMoveAskedBecause,
  type StepMoveKind,
  type StepMoveVocabulary,
} from "./stepMoveWords";

/** One move the Guide asked for, and what its target is called. */
export interface StepMoveRequest {
  readonly kind: StepMoveKind;
  readonly vocabulary: StepMoveVocabulary;
  /**
   * The run speaks a language other than English, so the learner's words are
   * not judged (user direction, 2026-10-11): any turn with a word in it, in any
   * script, asks. Only a turn of nothing but sounds is still refused, because
   * background noise is what this guardrail exists for and it reads the same
   * in every language. The word lists are English plus a handful of European
   * languages, and the transcript is pinned to English, so judging another
   * language's words would refuse real requests.
   */
  readonly skipWordCheck?: boolean;
}

/**
 * Why a move was refused. Each one tells the Guide something different:
 *   - `noise`: the turn held only sounds, so nobody asked anything and Spark says nothing.
 *   - `not_asked`: the learner said words that do not ask to move.
 *   - `scene_request`: the learner asked for a scene, which no step move gives them.
 *   - `unheard`: the transcript failed or did not arrive in time.
 *   - `no_turn`: the learner has said nothing since the project opened.
 */
export type StepMoveRefusalReason = "no_turn" | "noise" | "not_asked" | "scene_request" | "unheard";

export type StepMoveVerdict =
  | {
      readonly allowed: true;
      /** What the learner said, as read — for the log. */
      readonly heard: string;
      readonly because: StepMoveAskedBecause;
    }
  | {
      readonly allowed: false;
      readonly heard: string;
      readonly reason: StepMoveRefusalReason;
    };

export interface IStepMoveGuardrail {
  /** A turn the learner TYPED, or a `present_options` choice they tapped. */
  noteLearnerText(text: string): void;
  /** A spoken turn exists (its audio was committed); its words are still to come. */
  noteLearnerVoiceStarted(itemId: string): void;
  /** Part of a spoken turn's transcript. */
  noteLearnerVoiceDelta(itemId: string, delta: string): void;
  /** A spoken turn's finished transcript — empty for silence, null when transcription failed. */
  noteLearnerVoiceSettled(itemId: string, transcript: string | null): void;
  /**
   * A line Spark finished saying, narration included — for "what is the
   * learner's next turn answering?" (a question about moving on, or about
   * whether they have any questions).
   */
  noteSparkLine(text: string): void;
  /** A new project: nothing said before it can ask for a move in it. */
  reset(): void;
  /** Did the learner's latest turn ask for this move? Waits for a spoken turn's transcript. */
  checkAsync(request: StepMoveRequest): Promise<StepMoveVerdict>;
}

/**
 * How long a move waits for a spoken turn's transcript before it is refused as
 * unheard.
 *
 * ⚠ A STARTING value, not a measured one. The traces put the transcript from
 * ~0.8 s BEFORE the model's call to ~1.1 s AFTER it, and every check logs how
 * long it waited (`step_move.check` in the debug Log tab). Set this from a
 * device session's numbers. Too short refuses a real "next" and makes Spark ask
 * again; too long holds a real one up for that long.
 */
export const K_STEP_MOVE_TRANSCRIPT_WAIT_MS = 3000;

/**
 * Spoken turns remembered by item id. A transcript lands within a few seconds
 * of its turn, so a short window is plenty; the bound stops a long session
 * growing the map.
 */
const K_MAX_TRACKED_SPOKEN_TURNS = 16;

interface LearnerTurn {
  /** The settled words. Null while a spoken turn's transcript is on its way, or if it failed. */
  words: string | null;
  /** The transcript so far. Enough to ALLOW a move early, never to refuse one. */
  partial: string;
  /** Transcription failed: no words will ever arrive. */
  failed: boolean;
  /**
   * Spark's last line before this turn — what the turn is answering. Kept as
   * TEXT and read at check time rather than classified when the turn begins,
   * because whether it asked about moving depends on the step's own button
   * labels, which only the move being checked knows (AI-1418).
   */
  readonly sparkLineBefore: string;
  /** A refusal told Spark to ask about moving just before this turn — see `m_refusalAskedToConfirm`. */
  readonly followsRefusal: boolean;
}

export class StepMoveGuardrail implements IStepMoveGuardrail {
  private m_latest: LearnerTurn | null = null;
  private readonly m_spokenTurns = new Map<string, LearnerTurn>();
  /** Spark's most recent line. Consumed by the next learner turn. */
  private m_lastSparkLine = "";
  /**
   * A refusal told Spark to ask whether they want to move. Then ANY question
   * Spark asks next is that confirmation, whatever its wording. Consumed by the
   * next learner turn.
   */
  private m_refusalAskedToConfirm = false;
  private readonly m_waiters = new Set<() => void>();
  private readonly m_log: ((summary: string) => void) | null;
  private readonly m_waitMs: number;

  constructor(
    log: ((summary: string) => void) | null = null,
    waitMs: number = K_STEP_MOVE_TRANSCRIPT_WAIT_MS,
  ) {
    this.m_log = log;
    this.m_waitMs = waitMs;
  }

  noteLearnerText(text: string): void {
    this.beginTurn(text);
  }

  noteLearnerVoiceStarted(itemId: string): void {
    // Two server events announce the same spoken turn (the committed audio and
    // the conversation item), so the id is what keeps it one turn.
    if (this.m_spokenTurns.has(itemId)) return;
    this.m_spokenTurns.set(itemId, this.beginTurn(null));
    if (this.m_spokenTurns.size > K_MAX_TRACKED_SPOKEN_TURNS) {
      const oldest = this.m_spokenTurns.keys().next().value;
      if (oldest !== undefined) this.m_spokenTurns.delete(oldest);
    }
  }

  noteLearnerVoiceDelta(itemId: string, delta: string): void {
    const turn = this.m_spokenTurns.get(itemId);
    if (!turn || turn.words !== null) return;
    turn.partial += delta;
    this.notifyWaiters();
  }

  noteLearnerVoiceSettled(itemId: string, transcript: string | null): void {
    // A transcript for a turn whose start was never seen is still the learner
    // speaking, so it counts as a turn of its own rather than being dropped.
    if (!this.m_spokenTurns.has(itemId)) this.noteLearnerVoiceStarted(itemId);
    const turn = this.m_spokenTurns.get(itemId);
    if (!turn) return;
    if (transcript === null) {
      turn.failed = true;
    } else {
      turn.words = transcript;
    }
    this.notifyWaiters();
  }

  noteSparkLine(text: string): void {
    this.m_lastSparkLine = text;
  }

  reset(): void {
    this.m_latest = null;
    this.m_spokenTurns.clear();
    this.m_lastSparkLine = "";
    this.m_refusalAskedToConfirm = false;
    this.notifyWaiters();
  }

  async checkAsync(request: StepMoveRequest): Promise<StepMoveVerdict> {
    const startedAt = Date.now();
    const turn = this.m_latest;
    const verdict: StepMoveVerdict = turn
      ? await this.readTurnAsync(turn, request)
      : { allowed: false, heard: "", reason: "no_turn" };

    // Every refusal but noise tells Spark to ask whether they want to move, so
    // the question it asks next is a move question whatever its words. A scene
    // refusal does not: Spark offers next OR back there, and a bare "yes" to
    // two choices picks neither.
    if (!verdict.allowed && verdict.reason !== "noise" && verdict.reason !== "scene_request") {
      this.m_refusalAskedToConfirm = true;
    }

    this.m_log?.(
      `kind=${request.kind} ` +
        (verdict.allowed ? `allowed because=${verdict.because}` : `refused reason=${verdict.reason}`) +
        ` heard=${JSON.stringify(verdict.heard)} waited=${Date.now() - startedAt}ms`,
    );
    return verdict;
  }

  // -------------------------------------------------------------------------

  private beginTurn(words: string | null): LearnerTurn {
    const turn: LearnerTurn = {
      words,
      partial: "",
      failed: false,
      sparkLineBefore: this.m_lastSparkLine,
      followsRefusal: this.m_refusalAskedToConfirm,
    };
    this.m_lastSparkLine = "";
    this.m_refusalAskedToConfirm = false;
    this.m_latest = turn;
    return turn;
  }

  /**
   * The turn is pinned when the check starts. A newer turn arriving during the
   * wait is not the one the model acted on.
   */
  private async readTurnAsync(turn: LearnerTurn, request: StepMoveRequest): Promise<StepMoveVerdict> {
    const deadline = Date.now() + this.m_waitMs;
    for (;;) {
      if (turn.words !== null) return this.read(turn.words, turn, request);
      if (turn.failed) return { allowed: false, heard: turn.partial, reason: "unheard" };
      // Only a partial that has reached the end of a sentence can let the move
      // go early. "Go to the next" already asks, and the "scene" that turns it
      // into a request no move can grant is still on its way.
      if (endsSentence(turn.partial)) {
        const early = this.read(turn.partial, turn, request);
        if (early.allowed) return early;
      }
      const remaining = deadline - Date.now();
      if (remaining <= 0) return { allowed: false, heard: turn.partial, reason: "unheard" };
      await this.waitForUpdateAsync(remaining);
    }
  }

  private read(said: string, turn: LearnerTurn, request: StepMoveRequest): StepMoveVerdict {
    if (asksForScene(said, request.vocabulary)) return { allowed: false, heard: said, reason: "scene_request" };
    if (request.skipWordCheck) {
      return isSoundsOnly(said)
        ? { allowed: false, heard: said, reason: "noise" }
        : { allowed: true, heard: said, because: "other_language" };
    }
    const line = turn.sparkLineBefore;
    const verdict = readStepMoveWords(said, request.kind, request.vocabulary, {
      answersMoveQuestion:
        isMoveQuestion(line, request.vocabulary.moveLabels) ||
        isConfirmRequest(line) ||
        (turn.followsRefusal && endsWithQuestion(line)),
      answersQuestionsQuestion: isQuestionsQuestion(line),
    });
    if (verdict.asked) return { allowed: true, heard: said, because: verdict.because };
    return { allowed: false, heard: said, reason: verdict.pureNoise ? "noise" : "not_asked" };
  }

  private waitForUpdateAsync(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      const wake = (): void => {
        clearTimeout(timer);
        this.m_waiters.delete(wake);
        resolve();
      };
      const timer = setTimeout(wake, timeoutMs);
      this.m_waiters.add(wake);
    });
  }

  private notifyWaiters(): void {
    for (const wake of [...this.m_waiters]) wake();
  }
}

/**
 * Whether a partial transcript ends on a sentence's closing mark. One that does
 * not may still have words to come; it waits for the settled transcript, which
 * costs the move the transcript's own latency and nothing else.
 */
function endsSentence(partial: string): boolean {
  return /[.!?]["'”’)]*$/.test(partial.trim());
}
